import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useT, type TKey } from '../../lib/i18n';
import { formatTime, SALON_TZ } from '../../lib/time';
import { Button } from '../ui';

export type WaitlistEntry = {
  id: string;
  part: 'morning' | 'afternoon' | 'evening' | 'any';
  services: string[];
  firstName: string;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  status: 'waiting' | 'notified';
  notifiedAt: string | null;
};

const key = (orgId: string, locationId: string | undefined, date: string) => ['waitlist', orgId, locationId, date];

/** Who is waiting for this day (only asked when the salon has the waitlist on). */
export function useWaitlist(orgId: string, locationId: string | undefined, date: string, enabled: boolean) {
  return useQuery({
    queryKey: key(orgId, locationId, date),
    queryFn: () => api<{ entries: WaitlistEntry[] }>(`/orgs/${orgId}/waitlist?locationId=${locationId}&date=${date}`).then((r) => r.entries),
    enabled: enabled && !!locationId,
  });
}

/** The list in the side panel: who, when they can, and a way to reach them or take them off. */
export function WaitlistList({ orgId, locationId, date, entries }: { orgId: string; locationId: string; date: string; entries: WaitlistEntry[] }) {
  const t = useT();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api(`/orgs/${orgId}/waitlist/${id}/remove`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(orgId, locationId, date) }),
  });
  if (entries.length === 0) return <p className="text-muted">{t('waitlist.empty')}</p>;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">{t('waitlist.intro')}</p>
      <ul className="flex flex-col divide-y divide-line rounded-[var(--radius-card)] border border-line bg-surface">
        {entries.map((e) => (
          <li key={e.id} className="flex flex-col gap-1.5 p-4">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold">{[e.firstName, e.lastName].filter(Boolean).join(' ')}</span>
              <span className="tag shrink-0">{t(`waitlist.part.${e.part}` as TKey)}</span>
            </div>
            {e.services.length > 0 && <span className="text-sm">{e.services.join(' + ')}</span>}
            <span className="text-sm text-muted">{e.status === 'notified' && e.notifiedAt ? t('waitlist.mailed', { time: formatTime(e.notifiedAt, SALON_TZ) }) : t('waitlist.waiting')}</span>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {e.phone && (
                <a href={`tel:${e.phone}`} className="font-medium text-accent-strong hover:underline">
                  {e.phone}
                </a>
              )}
              {e.email && (
                <a href={`mailto:${e.email}`} className="font-medium text-accent-strong hover:underline">
                  {e.email}
                </a>
              )}
              <Button variant="ghost" className="ml-auto min-h-9 px-3 text-sm" disabled={remove.isPending} onClick={() => remove.mutate(e.id)}>
                {t('waitlist.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
